"use client";

import React from "react";
import { Button } from "../common/button";

export interface Stat {
  value: string;
  label: string;
}

export interface FinalCTAWithStatsProps {
  title: string;
  subtitle?: string;
  stats?: Stat[];
  primaryCTA: {
    text: string;
    onClick?: () => void;
  };
  secondaryCTA?: {
    text: string;
    onClick?: () => void;
  };
  bgGradient?: string;
}

export function FinalCTAWithStats({
  title,
  subtitle,
  stats,
  primaryCTA,
  secondaryCTA,
  bgGradient = "from-primary-600 to-primary-700"
}: FinalCTAWithStatsProps) {
  return (
    <section className={`relative py-20 md:py-28 bg-gradient-to-br ${bgGradient} text-white overflow-hidden`}>
      {/* Decorative elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2"></div>
      </div>
      
      <div className="relative max-w-4xl mx-auto px-4 text-center">
        <h2 className="text-3xl md:text-5xl font-bold mb-6">
          {title}
        </h2>
        {subtitle && (
          <p className="text-xl md:text-2xl text-white/90 mb-10 max-w-2xl mx-auto">
            {subtitle}
          </p>
        )}
        
        {/* Stats */}
        {stats && stats.length > 0 && (
          <div className={`grid grid-cols-${stats.length} gap-8 max-w-2xl mx-auto mb-10`}>
            {stats.map((stat, idx) => (
              <div key={idx}>
                <p className="text-4xl font-bold mb-1">{stat.value}</p>
                <p className="text-sm text-white/80">{stat.label}</p>
              </div>
            ))}
          </div>
        )}
        
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button 
            variant="secondary" 
            size="lg" 
            className="min-w-[200px] bg-white text-primary-600 hover:bg-gray-100"
            onClick={primaryCTA.onClick}
          >
            {primaryCTA.text}
          </Button>
          {secondaryCTA && (
            <Button 
              variant="outline" 
              size="lg" 
              className="min-w-[200px] border-white text-white hover:bg-white/10"
              onClick={secondaryCTA.onClick}
            >
              {secondaryCTA.text}
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}